import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useAuth } from '../contexts/AuthContext'
import { useEventFetcher } from '../hooks/useEventFetcher'
import { useSchedulePlanner } from '../hooks/useSchedulePlanner'

import LoadingSpinner from '../components/UI/LoadingSpinner'
import StatusMessage from '../components/UI/StatusMessage'
import GlassPanel from '../components/UI/GlassPanel'
import ScheduleGrid from '../components/Schedule/ScheduleGrid'
import ConflictList from '../components/Schedule/ConflictList'
import SetTimeEditor from '../components/Schedule/SetTimeEditor'
import StageEditor from '../components/Schedule/StageEditor'
import {
  conflictKeySet,
  findGaps,
  formatDuration,
  plannedMinutes
} from '../utils/scheduleUtils'

/**
 * Per-event schedule planner.
 *
 * Lives on its own route rather than inside EventDetail: a to-scale timeline
 * needs the full page width, and the planner is a distinct mode of use from
 * reading an event back.
 */
const SchedulePlanner = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const { event, setEvent, loading, error: fetchError } = useEventFetcher(id)
  const {
    days,
    conflicts,
    untimedSlots,
    saving,
    error,
    success,
    togglePick,
    cyclePriority,
    saveSetTimes,
    saveStages,
    stages
  } = useSchedulePlanner(id, event, setEvent)

  const [activeDay, setActiveDay] = useState(null)
  const [editingTimes, setEditingTimes] = useState(false)
  const [editingStages, setEditingStages] = useState(false)

  // Default to the first day that has sets; re-run when the lineup arrives.
  useEffect(() => {
    if (!activeDay && days.length > 0) setActiveDay(days[0].dayKey)
  }, [days, activeDay])

  const isOwner = event?.createdBy?._id?.toString() === user?.id?.toString()

  const currentDay = useMemo(
    () => days.find(day => day.dayKey === activeDay) || days[0] || null,
    [days, activeDay]
  )

  const conflictKeys = useMemo(() => conflictKeySet(conflicts), [conflicts])
  const dayConflicts = useMemo(
    () => conflicts.filter(conflict => conflict.dayKey === currentDay?.dayKey),
    [conflicts, currentDay]
  )

  const gaps = useMemo(
    () => (currentDay ? findGaps(currentDay.slots) : []),
    [currentDay]
  )
  const planned = currentDay ? plannedMinutes(currentDay.slots) : 0
  const pickedCount = currentDay ? currentDay.slots.filter(slot => slot.picked).length : 0

  if (loading) return <LoadingSpinner text="Loading schedule..." />

  if (fetchError && !event) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <StatusMessage type="error" message={fetchError} />
        <button
          onClick={() => navigate('/passport')}
          className="mt-6 bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg transition-all duration-300"
        >
          ← Back to Passport
        </button>
      </div>
    )
  }

  if (!event) return null

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => navigate(`/event/${id}`)}
            className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg border border-white/20 hover:border-white/40 transition-all duration-300"
          >
            ← Back to Event
          </button>

          {isOwner && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => { setEditingStages(open => !open); setEditingTimes(false) }}
                className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg border border-white/20 transition-all duration-300"
              >
                {editingStages ? 'Close stages' : '🎪 Stages'}
              </button>
              <button
                onClick={() => { setEditingTimes(open => !open); setEditingStages(false) }}
                className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-300"
              >
                {editingTimes ? 'Close editor' : '🕒 Edit set times'}
              </button>
            </div>
          )}
        </div>

        <div>
          <h1 className="text-3xl font-bold text-white">{event.name}</h1>
          <p className="text-gray-300 mt-1">Schedule planner</p>
        </div>

        <StatusMessage type="success" message={success} />
        <StatusMessage type="error" message={error} />

        {/* Stage editor */}
        {editingStages && (
          <GlassPanel>
            <h2 className="text-xl font-semibold text-white mb-4">🎪 Stages</h2>
            <StageEditor
              stages={stages}
              onSave={saveStages}
              saving={saving}
              onClose={() => setEditingStages(false)}
            />
          </GlassPanel>
        )}

        {/* Set-time editor */}
        {editingTimes && (
          <GlassPanel>
            <h2 className="text-xl font-semibold text-white mb-4">🕒 Set times</h2>
            <SetTimeEditor
              event={event}
              stages={stages}
              onSave={saveSetTimes}
              saving={saving}
              onClose={() => setEditingTimes(false)}
            />
          </GlassPanel>
        )}

        {/* Empty state: a lineup exists but nobody has entered times yet. */}
        {days.length === 0 && !editingTimes && (
          <GlassPanel>
            <div className="text-center py-8">
              <div className="text-5xl mb-4">🗓</div>
              <h2 className="text-xl font-semibold text-white mb-2">No set times yet</h2>
              <p className="text-gray-300 max-w-md mx-auto">
                {untimedSlots.length > 0
                  ? `This event has ${untimedSlots.length} ${untimedSlots.length === 1 ? 'artist' : 'artists'} but no set times. Add times to build your timeline.`
                  : 'Add artists to this event first, then set their times here.'}
              </p>
              {isOwner && untimedSlots.length > 0 && (
                <button
                  onClick={() => setEditingTimes(true)}
                  className="mt-5 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-5 rounded-lg transition-all duration-300"
                >
                  🕒 Add set times
                </button>
              )}
              <p className="text-sm text-gray-400 mt-4">
                Or upload an updated flyer on the event page — set times printed on it are picked up automatically.
              </p>
            </div>
          </GlassPanel>
        )}

        {/* Planner */}
        {days.length > 0 && (
          <>
            {days.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {days.map(day => {
                  const dayPicked = day.slots.filter(slot => slot.picked).length
                  const hasConflict = conflicts.some(conflict => conflict.dayKey === day.dayKey)

                  return (
                    <button
                      key={day.dayKey}
                      onClick={() => setActiveDay(day.dayKey)}
                      className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all duration-200 ${
                        currentDay?.dayKey === day.dayKey
                          ? 'bg-purple-600 text-white shadow-lg'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      {day.label}
                      {dayPicked > 0 && (
                        <span className="ml-2 text-xs opacity-80">{dayPicked}</span>
                      )}
                      {hasConflict && <span className="ml-1 text-red-300">⚠</span>}
                    </button>
                  )
                })}
              </div>
            )}

            {currentDay && (
              <GlassPanel className="!p-4 sm:!p-6">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <h2 className="text-xl font-semibold text-white">{currentDay.label}</h2>
                  <div className="text-sm text-gray-300 flex items-center gap-3 flex-wrap">
                    <span>{pickedCount} of {currentDay.slots.length} sets</span>
                    {planned > 0 && <span className="text-gray-400">· {formatDuration(planned)} planned</span>}
                  </div>
                </div>

                <p className="text-xs text-gray-400 mb-4">
                  Stages down the side, time across the top. Tap a set to add it
                  to your plan — clashes show as blocks in the same column.
                </p>

                <ScheduleGrid
                  event={event}
                  daySlots={currentDay.slots}
                  stages={stages}
                  conflictKeys={conflictKeys}
                  onToggle={togglePick}
                  onCyclePriority={cyclePriority}
                  readOnly={!isOwner}
                />
              </GlassPanel>
            )}

            {dayConflicts.length > 0 && <ConflictList conflicts={dayConflicts} />}

            {gaps.length > 0 && (
              <div className="bg-white/5 border border-white/15 rounded-xl p-4">
                <h3 className="font-semibold text-white mb-2">☕ Breaks</h3>
                <div className="text-sm text-gray-300 space-y-1">
                  {gaps.map(gap => (
                    <div key={gap.id}>{formatDuration(gap.minutes)} free</div>
                  ))}
                </div>
              </div>
            )}

            {untimedSlots.length > 0 && (
              <div className="bg-white/5 border border-white/15 rounded-xl p-4">
                <h3 className="font-semibold text-white mb-2">
                  ⏰ {untimedSlots.length} {untimedSlots.length === 1 ? 'artist' : 'artists'} without a set time
                </h3>
                <p className="text-sm text-gray-300 mb-3">
                  These can't be placed on the timeline yet.
                </p>
                <div className="flex flex-wrap gap-2">
                  {untimedSlots.map(slot => (
                    <span
                      key={slot.key}
                      className="text-sm bg-white/10 border border-white/15 rounded-full px-3 py-1 text-gray-200"
                    >
                      {slot.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default SchedulePlanner
